import { useContext, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { GlobalContext } from '../GlobalContext'
import Button from '../formfields/Button'
import Spinner from '../ui-elements/Spinner'
import { subscribe } from '../utils/pubsub'
import ProcessSummaryCheckPeriods from './ProcessSummaryCheckPeriods'
import ProcessSummaryNoChecklist from './ProcessSummaryNoChecklist'
import YellowAlert from '../ui-elements/YellowAlert'
import ProcessStageIndicator from './ProcessStageIndicator'
import FooterNavButtonFrame from '../shared-components/FooterNavButtonFrame'
import StartProcess from './StartProcess'
import ProcessInstanceChooser from './ProcessInstanceChooser'



type Props = {
}


// countdown timer compoentent - gets given time of next check in milliseconds as property and 
// counts down in minutes, updating every 20 seconds or so

function CountdownTimer({
    timeToNextCheck
}: { timeToNextCheck: number }) {

    const [minutesToNextCheck, setMinutesToNextCheck] = useState<number | null>(null)
    const [timerId, setTimerId] = useState<NodeJS.Timeout | null>(null)
    const { t } = useContext(GlobalContext)

    useEffect(() => {
        if (timerId) {
            clearInterval(timerId)
        }
        const myTimerId = setInterval(() => {
            updateMinutesToNextCheck(timeToNextCheck)
        }, 20000)

        setTimerId(myTimerId)
        updateMinutesToNextCheck(timeToNextCheck)

        return () => {
            timerId && clearInterval(timerId)
        }
    }, [timeToNextCheck])

    function updateMinutesToNextCheck(timestamp_utc_milliseconds: number) {
        //  local time might not be utc, need to get time in utc timezone
        //const now = new Date().getTime() + new Date().getTimezoneOffset() * 60000
        const now = Date.now()
        const minutes = Math.floor((timestamp_utc_milliseconds - now) / 60000)
        setMinutesToNextCheck(minutes)
    }



    //cancel timer when component unmounts

    if (minutesToNextCheck && minutesToNextCheck > 1) {
        return <YellowAlert fullwidth={true}>{t('ProcessPages.ProcessSummary.NextChecksDueInMinutes', { minutes: minutesToNextCheck })}</YellowAlert>
    } else {
        return <YellowAlert fullwidth={true}>{t('ProcessPages.ProcessSummary.NextChecksDueSoon')}</YellowAlert>

    }
}

function ProcessSummary({
}: Props) {

    const { processId, processRecordId } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const fromChecklist = location.state?.fromChecklist === true
    const {
        tableData,
        sendMessageToWebsocket,
        setShowModal,
        processData,
        t
    } = useContext(GlobalContext)
    const [pendingProcessDefinitionIds, setPendingProcessDefinitionIds] = useState<string[]>([])
    const [requestedProcessDefinitionIds, setRequestedProcessDefinitionIds] = useState<string[]>([])

    useEffect(() => {
        if (
            tableData && processId &&
            (!tableData.ProcessDefinitions || !tableData.ProcessDefinitions[processId])) {
            setPendingProcessDefinitionIds([...pendingProcessDefinitionIds, processId])
        }
    }, [tableData])


    useEffect(() => {
        if (pendingProcessDefinitionIds && pendingProcessDefinitionIds.length > 0) {
            for (const processId of pendingProcessDefinitionIds) {
                if (!requestedProcessDefinitionIds.includes(processId)) {
                    fetchProcessDefinition(processId)
                    setRequestedProcessDefinitionIds([...requestedProcessDefinitionIds, processId])
                }
            }
        }
    }, [pendingProcessDefinitionIds])

    const fetchProcessDefinition = (processId: string) => {
        let requestAssets = JSON.stringify({
            action: "processesClient",
            subAction: "fetchProcessDefinition",
            processId: processId
        })
        sendMessageToWebsocket(requestAssets)
    }


    const isViewingSpecificInstance = !!processRecordId
    const completeRoute = isViewingSpecificInstance
        ? `/process/${processId}/record/${processRecordId}/complete`
        : `/process/${processId}/complete`

    const completeStageCustomNextStep = (nextStageId: string, processRecordId: string) => {
        setShowConfirmEndStage(false)
        setShowModal({ 'spinner': t('General.loading') })
        let payload = JSON.stringify({
            action: "processesClient",
            subAction: "completeStageCustomNextStep",
            nextStageId: nextStageId,
            processRecordId: processRecordId
        })

        const unsubscribeUpdate = subscribe("completeStageCustomNextStepResponse", data => {
            if (data && data.processRecordId === processRecordId) {
                setShowModal(null)
                if (data.nextChecklistPeriod) {
                    navigate(`/checklists/${data.nextChecklistPeriod}`)
                } else if (!data.nextStageId) {
                    navigate(completeRoute)
                }
            }
            unsubscribeUpdate()
        })
        sendMessageToWebsocket(payload)
    }

    const completeStageDefault = (processRecordId: string) => {
        setShowConfirmEndStage(false)
        setShowModal({ 'spinner': t('General.loading') })
        let payload = JSON.stringify({
            action: "processesClient",
            subAction: "completeStageDefaultNextStep",
            processRecordId: processRecordId,
            currentStageId: currentStageId
        })

        const unsubscribeUpdate = subscribe("completeStageDefaultNextStepResponse", data => {
            if (data && data.processRecordId === processRecordId) {
                setShowModal(null)
                if (data.nextChecklistPeriod) {
                    navigate(`/checklist/${data.nextChecklistPeriod}`)
                } else if (!data.nextStageId) {
                    navigate(completeRoute)
                }
            }
            unsubscribeUpdate()
        })
        sendMessageToWebsocket(payload)
    }


    const [showConfirmEndStage, setShowConfirmEndStage] = useState(false)
    const [selectedStepId, setSelectedStepId] = useState<string | null>(null)
    const wasInProgress = useRef<boolean | null>(null)
    const lastViewKey = useRef<string | null>(null)
    const thisProcessData = processId && processData && processData[processId]
    const runningRecordIds = (thisProcessData && thisProcessData.processRecords) ? Object.keys(thisProcessData.processRecords) : []
    // landed here via the process's QR code (no specific instance chosen) with more than one instance
    // running - let the user pick which one, rather than guessing which one "the" instance is
    const showInstanceChooser = !isViewingSpecificInstance && runningRecordIds.length > 1
    // when viewing a specific instance, resolve that instance's data; otherwise fall back to the
    // legacy singular fields on thisProcessData (mirrored from the single in-progress record, if any)
    const activeRecordData = thisProcessData && (isViewingSpecificInstance
        ? (thisProcessData.processRecords && thisProcessData.processRecords[processRecordId])
        : thisProcessData)
    const recordInProgress = !!activeRecordData
    const inProgress = isViewingSpecificInstance ? recordInProgress : thisProcessData && thisProcessData.InProgress === true
    const currentStageId = activeRecordData && activeRecordData['processRecord'] && activeRecordData['processRecord']['CurrentStageId']
    const thisStageDetails = thisProcessData && thisProcessData['Stages'] && currentStageId && thisProcessData['Stages'][currentStageId]
    const isOngoingStage = thisStageDetails && thisStageDetails['Behaviour'] === 'ongoing'
    const hasChecklists = thisStageDetails && thisStageDetails['Checklists'] && thisStageDetails['Checklists'].length > 0
    const thisStageProcessRecord = activeRecordData && activeRecordData['processRecord'] && activeRecordData['processRecord']['StageProgress'][currentStageId]
    const nextChecksDue = thisStageProcessRecord && thisStageProcessRecord['NextRepetitionTime']
    const customNextSteps = activeRecordData && activeRecordData['NextSteps'] && Object.keys(activeRecordData['NextSteps']).length > 0

    // ProcessSummary stays mounted across route-param changes (e.g. navigating from one instance's
    // URL to another's after starting a new one), so track which instance is being viewed and only
    // treat "not in progress" as a genuine completion when it happens while still looking at the
    // same instance - otherwise a freshly-navigated-to instance (whose record hasn't streamed into
    // the app's data yet) reads as "not in progress" and gets mistaken for one that just finished
    const viewKey = isViewingSpecificInstance ? `${processId}/${processRecordId}` : `${processId}`

    useEffect(() => {
        if (!thisProcessData || showInstanceChooser) return
        const isNewView = lastViewKey.current !== viewKey
        lastViewKey.current = viewKey

        if (!inProgress) {
            // came from a checklist sign-off (process may have just completed)
            if (fromChecklist) {
                navigate(completeRoute, { replace: true })
                return
            }
            // was in-progress during this session and just transitioned to complete
            if (!isNewView && wasInProgress.current === true) {
                navigate(completeRoute, { replace: true })
                return
            }
        }
        wasInProgress.current = inProgress
    }, [thisProcessData, activeRecordData, showInstanceChooser, viewKey])


    if (!thisProcessData) {
        return <div className={`w-full flex-1 h-full flex flex-col items-center gap-2`}>
            <div className={`max-w-3xl w-full px-5 py-5 flex flex-col gap-4`}>
                <Spinner size='large'>{t('ProcessPages.common.loading')}</Spinner>
            </div>
        </div>
    }

    if (showInstanceChooser) {
        return <div className={`w-full flex-1 h-full flex flex-col items-center gap-2`}>
            <div className={`max-w-3xl w-full px-5 py-10 flex flex-col gap-6 items-center`}>
                <h3 className={`text-2xl font-righteous text-brandblue`}>{thisProcessData.DisplayName}</h3>
                <ProcessInstanceChooser processId={processId as string} />
                {thisProcessData.AllowMultipleInstances === true && <StartProcess
                    thisProcessData={thisProcessData}
                    processId={processId}
                />}
            </div>
        </div>
    }

    return <div className={`w-full flex-1 h-full flex flex-col items-center gap-2`}>


        {inProgress && <div className={`w-full`}>
            <div className={`w-full flex flex-row gap-4 items-center bg-white justify-center arrow-white drop-shadow-xl border-b-2 border-gray-300`} >
                <div className={`max-w-3xl w-full px-5 py-2 flex flex-col gap-4 items-center justify-center text-center`}>

                    <ProcessStageIndicator
                        thisProcessData={{ ...thisProcessData, processRecord: activeRecordData && activeRecordData.processRecord }}
                    />
                </div>
            </div>
        </div>}




        <div className={`
        ${inProgress ? ' pt-8 pb-20 ' : ' py-10'}
        max-w-3xl w-full px-5 flex flex-col gap-4
            `}>

            {showConfirmEndStage && <div className={`w-full flex flex-col gap-6 items-center text-center`}>
                <h3 className={`text-2xl font-righteous text-brandblue`}>{t('ProcessPages.ProcessSummary.areYouSureProceed')}</h3>
                <p>{t('ProcessPages.ProcessSummary.movingToAnotherStage', { stageName: thisStageDetails && thisStageDetails['DisplayName'] })}</p>

                <div className={`w-full flex flex-col gap-2`}>
                    {!customNextSteps && <Button
                        onClick={(e: any) => { completeStageDefault(activeRecordData.processRecord.Id) }}
                        text={t('ProcessPages.ProcessSummary.confirm')}
                        fullwidth={true}
                        size={`big`}
                        variant={`primary`}
                    />}

                    {customNextSteps && selectedStepId && <Button
                        onClick={(e: any) => { completeStageCustomNextStep(selectedStepId, activeRecordData.processRecord.Id) }}
                        text={t('ProcessPages.ProcessSummary.confirm')}
                        fullwidth={true}
                        size={`big`}
                        variant={`primary`}
                    />}

                    <Button
                        onClick={(e: any) => { setShowConfirmEndStage(false); setSelectedStepId(null) }}
                        text={t('ProcessPages.ProcessSummary.cancel')}
                        fullwidth={true}
                        size={`big`}
                        variant={`white`}
                    />
                </div>

            </div>}


            {!showConfirmEndStage && <div className={`w-full flex flex-col gap-6 items-center`}>


                {!inProgress && !isViewingSpecificInstance && <StartProcess
                    thisProcessData={thisProcessData}
                    processId={processId}
                />}



                {nextChecksDue && <CountdownTimer timeToNextCheck={nextChecksDue} />}

                {inProgress && hasChecklists && <ProcessSummaryCheckPeriods />}

                {inProgress && isOngoingStage && !hasChecklists && (
                    <ProcessSummaryNoChecklist
                        stageProcessRecord={thisStageProcessRecord}
                        stageName={thisStageDetails && thisStageDetails['DisplayName']}
                        customNextSteps={!!customNextSteps}
                        nextSteps={activeRecordData.NextSteps || null}
                        onCompleteDefault={() => completeStageDefault(activeRecordData.processRecord.Id)}
                        onCompleteCustomNextStep={(stepId) => completeStageCustomNextStep(stepId, activeRecordData.processRecord.Id)}
                    />
                )}

                {isOngoingStage && hasChecklists && <FooterNavButtonFrame title={t('ProcessPages.ProcessSummary.nextSteps')}>
                    {!customNextSteps && <Button
                        onClick={(e: any) => setShowConfirmEndStage(true)}
                        text={t('ProcessPages.ProcessSummary.completeThisStage')}
                        fullwidth={true}
                        size={`big`}
                        variant={`primary`}
                    />}

                    {customNextSteps && Object.keys(activeRecordData.NextSteps).map((step_id: any, index: number) => {
                        const thisStep = activeRecordData.NextSteps[step_id]
                        return <Button
                            key={index}
                            onClick={(e: any) => {
                                setSelectedStepId(step_id)
                                setShowConfirmEndStage(true)
                            }}
                            text={t('ProcessPages.ProcessSummary.nextStepName', { name: thisStep.NextStepName })}
                            fullwidth={true}
                            size={`big`}
                            variant={`primary`}
                        />
                    })}
                </FooterNavButtonFrame>}
            </div>}
        </div>



    </div >

}
export default ProcessSummary